import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export const useOAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const connectService = async (service, assistantPhone = null) => {
    console.log('useOAuth: connectService called with service:', service);
    setLoading(true);
    setError(null);

    try {
      // Get Cognito JWT token
      console.log('useOAuth: fetching auth session');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call OAuth endpoint with cache-busting and optional assistant phone
      console.log('useOAuth: making fetch request to OAuth API');
      const params = new URLSearchParams({ t: Date.now() });
      if (assistantPhone) params.set('assistant_phone', assistantPhone);
      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/${service}/auth?${params}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        throw new Error('UNAUTHORIZED');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, response.statusText, errorText);
        throw new Error(`Failed to get OAuth URL: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('useOAuth: received OAuth response:', data);
      
      // Open popup window
      console.log('useOAuth: opening popup with URL:', data.auth_url);
      const popup = window.open(
        data.auth_url,
        'oauth-popup',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      // Monitor popup with postMessage
      return new Promise((resolve, reject) => {
        // Listen for postMessage from popup
        const messageHandler = (event) => {
          if (event.data && event.data.type === 'oauth_success') {
            window.removeEventListener('message', messageHandler);
            clearInterval(checkClosed);
            resolve({ success: true, service: event.data.service, message: event.data.message });
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Fallback: check if popup is closed (for browsers that allow it)
        const checkClosed = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkClosed);
              window.removeEventListener('message', messageHandler);
              resolve({ success: true, service: data.service });
            }
          } catch (e) {
            // Ignore COOP errors
            console.error.message = e.message;
          }
        }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          if (popup && !popup.closed) {
            popup.close();
          }
          reject(new Error('OAuth process timed out'));
        }, 300000);
      });

    } catch (err) {
      console.error('OAuth error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { connectService, loading, error };
};